import { manageDocument } from '../services/documentWorkflowService';
import { uploadSingleDocument, isErrorResponse } from '../services/docStorageService';
import { DocumentDownloadLink } from '../components/DocumentDownloadLink';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSupabase } from '../contexts/SupabaseContext';
import AdminNavBar from '../components/AdminNavBar';
import ShareAccessManager from '../components/submissions/ShareAccessManager';
import { useAuth } from '../hooks/useAuth';
import { Submission, Document, DecisionType, ReviewDecision, ReviewDecisionInsert } from '../lib/supabaseClient';
import FeedbackAndResponses from '../components/FeedbackAndResponses';
import { ReviewCycle } from '../types/submission';
import withIdleTimer from '../hoc/withIdleTimer';
import { isContinuingReviewsUiEnabled } from '../services/featureFlagService';
import {
  updateContinuingReviewDecisionMetadata,
  listRegulatoryBasisCodes,
} from '../services/continuingReviewService';
import { adminReviewRoute } from '../utils/submissionRoutes';

// Helper function to determine the bucket name based on document type
const getDocumentBucket = (docType: string): string => {
  return ['submission_form_markdown', 'submission_form_pdf', 'decision_letter', 'amendment_decision_letter', 'stamped_icf'].includes(docType)
    ? 'tempus-created-docs'
    : 'user-uploaded-docs';
};

const getLocalIsoDate = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offsetMs).toISOString().split('T')[0];
};

const USER_DOCUMENT_TYPE_OPTIONS = [
  'doc_protocol',
  'doc_consent_form',
  'doc_human_subject_training',
  'doc_ind_fda_letter',
  'doc_irb_auth_agreement',
  'doc_administrative_action',
  'doc_study_personnel_resumes',
  'doc_investigator_brochure',
  'doc_recruitment_materials',
  'doc_data_collection_tools',
  'doc_additional_files',
  'doc_fwa_documentation',
];

const ADMIN_DOCUMENT_UPLOAD_TYPE_OPTIONS = [
  ...USER_DOCUMENT_TYPE_OPTIONS,
  'amendment_decision_letter',
];

const USER_DOCUMENT_UPLOAD_BUCKET = 'user-uploaded-docs';
const SYSTEM_DOCUMENT_UPLOAD_BUCKET = 'tempus-created-docs';
const MAX_ADMIN_DOCUMENT_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;

const CONSENT_TYPE_EDIT_OPTIONS = ['doc_consent_form', 'doc_additional_files'];

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  amendment_decision_letter: 'Amendment Decision Letter',
  doc_protocol: 'Study Protocol',
  doc_consent_form: 'Informed Consent Form',
  doc_human_subject_training: 'Human Subject Training Documentation',
  doc_ind_fda_letter: 'FDA IND / Expanded-Access Correspondence',
  doc_irb_auth_agreement: 'IRB Authorization Agreement',
  doc_administrative_action: 'Administrative Action Documentation',
  doc_study_personnel_resumes: 'Study Personnel Resumes/CVs',
  doc_investigator_brochure: "Investigator's Brochure",
  doc_recruitment_materials: 'Participant Recruitment Materials',
  doc_data_collection_tools: 'Data Collection Tools',
  doc_additional_files: 'Additional Files',
  doc_fwa_documentation: 'Federal Wide Assurance (FWA) Documentation',
};

const getTypeEditOptions = (docType: string | null) => {
  if (docType === 'doc_consent_form') {
    return CONSENT_TYPE_EDIT_OPTIONS;
  }
  return USER_DOCUMENT_TYPE_OPTIONS;
};

const getSafeFileExtension = (filename: string) => {
  const extension = filename.split('.').pop()?.toLowerCase();
  return extension && /^[a-z0-9]+$/.test(extension) ? extension : 'pdf';
};

const getAdminUploadBucket = (docType: string) => {
  return docType === 'amendment_decision_letter'
    ? SYSTEM_DOCUMENT_UPLOAD_BUCKET
    : USER_DOCUMENT_UPLOAD_BUCKET;
};

// Type-aware display label. Defaults to the initial-submission label so existing
// (NEW_SUBMISSION) copy is unchanged.
const SUBMISSION_TYPE_LABELS: Record<string, string> = {
  NEW_SUBMISSION: 'Initial Submission',
  AMENDMENT: 'Amendment',
  CONTINUING_REVIEW: 'Continuing Review',
};

const getSubmissionTypeLabel = (submissionType?: string | null): string =>
  SUBMISSION_TYPE_LABELS[submissionType || ''] || 'Initial Submission';

// Controlled "continuing review not required" reasons (mirror server validation).
const CONTINUING_REVIEW_NOT_REQUIRED_REASONS: Array<{ value: string; label: string }> = [
  { value: 'HHS_ELIGIBLE_EXPEDITED', label: 'Eligible for expedited review (HHS)' },
  { value: 'HHS_LIMITED_IRB_REVIEW', label: 'Limited IRB review (HHS)' },
  { value: 'HHS_DATA_ANALYSIS_ONLY', label: 'Data analysis only (HHS)' },
  { value: 'HHS_FOLLOWUP_CLINICAL_DATA_ONLY', label: 'Follow-up clinical data only (HHS)' },
  { value: 'IRB_DETERMINED_NOT_REQUIRED', label: 'IRB determined not required' },
];

type RegulatoryBasisCodeRow = {
  code: string;
  authority: string;
  description: string;
  requires_continuing_review: boolean;
  allows_not_required_reason: boolean;
};

// Add whole months to an ISO yyyy-mm-dd date string (UTC-safe). Used for the
// client-side pre-check that expiry <= approval + interval months.
const addMonthsToIsoDate = (isoDate: string, months: number): string => {
  const [year, month, day] = isoDate.split('-').map((part) => parseInt(part, 10));
  if (!year || !month || !day) {
    return isoDate;
  }
  const base = new Date(Date.UTC(year, month - 1, day));
  base.setUTCMonth(base.getUTCMonth() + months);
  return base.toISOString().split('T')[0];
};

const AdminReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, signOut, loading: authLoading, sessionExpired } = useAuth();

  // Memoize the Supabase client
  const supabase = useSupabase();

  const [submission, setSubmission] = useState<Submission | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [inactiveDocuments, setInactiveDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updateStatus, setUpdateStatus] = useState<'idle' | 'updating' | 'success' | 'error'>('idle');

  // Add refs to track state and prevent duplicate operations
  const isFetchingRef = useRef(false);
  const userIdRef = useRef<string | null>(null);

  // Editable fields state
  const [classification, setClassification] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [readyForPiApproval, setReadyForPiApproval] = useState<boolean>(false);
  const [piApprovalStatusMsg, setPiApprovalStatusMsg] = useState<string>('');
  const [piApprovalMsgStatus, setPiApprovalMsgStatus] = useState<'idle' | 'updating' | 'success' | 'error'>('idle');
  const [paymentCompleted, setPaymentCompleted] = useState<boolean>(false);
  const [piApproval, setPiApproval] = useState<boolean>(false);

  // Review data state
  const [cycle, setCycle] = useState<string>('');
  const [reviewSubmitStatus, setReviewSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  // New state for feedback items
  const [feedbackItems, setFeedbackItems] = useState<Array<{ type: string, text: string }>>([
    { type: 'required_change', text: '' }
  ]);

  // Decision data state
  const [decision, setDecision] = useState<string>('');
  const [decisionData, setDecisionData] = useState<string>('');
  const [decisionSubmitStatus, setDecisionSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [approvalDate, setApprovalDate] = useState<string>(getLocalIsoDate());
  const [expiryDate, setExpiryDate] = useState<string>('');

  const [reviewDecision, setReviewDecision] = useState<ReviewDecision | null>(null);

  // Add decision letter file state
  const [decisionFile, setDecisionFile] = useState<File | null>(null);
  const [decisionFileError, setDecisionFileError] = useState<string | null>(null);
  const [fileUploading, setFileUploading] = useState(false);

  // Stamped ICF state (manual for conditional approvals)
  const [showStampedIcfModal, setShowStampedIcfModal] = useState<boolean>(false);
  const [stampedIcfMode, setStampedIcfMode] = useState<'existing' | 'upload'>('existing');
  const [stampedIcfFiles, setStampedIcfFiles] = useState<File[]>([]);
  const [stampedIcfFileError, setStampedIcfFileError] = useState<string | null>(null);
  const [stampedIcfStatus, setStampedIcfStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [stampedIcfError, setStampedIcfError] = useState<string | null>(null);
  const [stampedIcfApprovalDate, setStampedIcfApprovalDate] = useState<string>(getLocalIsoDate());
  const [stampedIcfExpiryDate, setStampedIcfExpiryDate] = useState<string>('');
  const [selectedExistingConsentIds, setSelectedExistingConsentIds] = useState<string[]>([]);

  const [documentActionInFlightId, setDocumentActionInFlightId] = useState<string | null>(null);
  const [documentTypeInFlightId, setDocumentTypeInFlightId] = useState<string | null>(null);
  const [adminUploadDocumentType, setAdminUploadDocumentType] = useState<string>('doc_protocol');
  const [adminUploadFiles, setAdminUploadFiles] = useState<File[]>([]);
  const [adminUploadStatus, setAdminUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [adminUploadError, setAdminUploadError] = useState<string | null>(null);
  const adminUploadIds = useRef(new WeakMap<File, string>());
  const adminDocumentFileInputRef = useRef<HTMLInputElement | null>(null);

  // Requires PI approval toggle (default: true)
  const [requiresPiApproval, setRequiresPiApproval] = useState<boolean>(true);

  // Collapsible sections state
  const [showActiveDocs, setShowActiveDocs] = useState<boolean>(false);
  const [showInactiveDocs, setShowInactiveDocs] = useState<boolean>(false);
  const [showSubmissionJson, setShowSubmissionJson] = useState<boolean>(false);
  const [showSourceSummary, setShowSourceSummary] = useState<boolean>(true);

  // Continuing-review decision metadata (additive; gated by the env UI flag).
  const crUiEnabled = isContinuingReviewsUiEnabled();
  const [crDetermination, setCrDetermination] = useState<'' | 'required' | 'not_required'>('');
  const [crIntervalMonths, setCrIntervalMonths] = useState<string>('');
  const [crRegulatoryBasisCodes, setCrRegulatoryBasisCodes] = useState<string[]>([]);
  const [crWindowDays, setCrWindowDays] = useState<string>('60');
  const [crBasis, setCrBasis] = useState<string>('');
  const [crRationale, setCrRationale] = useState<string>('');
  const [crNotRequiredReason, setCrNotRequiredReason] = useState<string>('');
  const [crMetadataError, setCrMetadataError] = useState<string | null>(null);
  const [regulatoryCodes, setRegulatoryCodes] = useState<RegulatoryBasisCodeRow[]>([]);
  const [regulatoryCodesLoaded, setRegulatoryCodesLoaded] = useState<boolean>(false);
  const [regulatoryCodesLoading, setRegulatoryCodesLoading] = useState<boolean>(false);
  const [regulatoryCodesRetry, setRegulatoryCodesRetry] = useState<number>(0);
  const regulatoryCodesRequestedRef = useRef<boolean>(false);

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Add check for auth loading and session expiration
  useEffect(() => {
    // If session is expired, redirect to auth page
    if (sessionExpired) {
      navigate('/signin');
    }
  }, [sessionExpired, navigate]);

  // Memoize the fetchSubmissionAndDocuments function
  const fetchSubmissionAndDocuments = useCallback(async () => {
    // Skip if already fetching or not authenticated
    if (isFetchingRef.current || !user?.id || !id) {
      return;
    }

    isFetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // Fetch submission with authenticated client
      const { data: submissionData, error: submissionError } = await supabase
        .from('submissions')
        .select('*')
        .eq('id', id)
        .single();

      if (submissionError) {
        setError(`Failed to fetch submission: ${submissionError.message}`);
        return;
      }

      if (!submissionData) {
        setError('Submission not found');
        return;
      }

      setSubmission(submissionData as unknown as Submission);
      setClassification(submissionData.classification || '');
      setStatus(submissionData.status || '');
      setReadyForPiApproval(!!submissionData.ready_for_pi_approval);
      setPiApprovalStatusMsg(submissionData.pi_approval_status_msg || '');
      setPaymentCompleted(!!submissionData.payment_completed);
      setPiApproval(!!submissionData.pi_approval);

      // Fetch active documents
      const { data: activeDocumentsData, error: activeDocumentsError } = await supabase
        .from('documents')
        .select('*')
        .eq('submission_id', id)
        .eq('is_active', true);

      if (activeDocumentsError) {
        console.error('Error fetching active documents:', activeDocumentsError);
      }

      setDocuments(activeDocumentsData as Document[] || []);

      // Fetch inactive documents
      const { data: inactiveDocumentsData, error: inactiveDocumentsError } = await supabase
        .from('documents')
        .select('*')
        .eq('submission_id', id)
        .eq('is_active', false);

      if (inactiveDocumentsError) {
        console.error('Error fetching inactive documents:', inactiveDocumentsError);
      }

      setInactiveDocuments(inactiveDocumentsData as Document[] || []);
    } catch (err: any) {
      setError(`Unexpected error: ${err.message}`);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [id, supabase, user?.id]);

  // Fetch submission only after auth is confirmed
  useEffect(() => {
    // Update userIdRef if user changes
    if (user?.id !== userIdRef.current) {
      userIdRef.current = user?.id || null;
    }

    // Skip fetching if auth is still loading
    if (authLoading) {
      return;
    }

    fetchSubmissionAndDocuments();
  }, [authLoading, fetchSubmissionAndDocuments, user?.id]);

  useEffect(() => {
    if (!submission?.id || !user?.id) {
      setReviewDecision(null);
      return;
    }

    const fetchReviewDecision = async () => {
      try {
        const { data, error } = await supabase
          .from('review_decisions')
          .select('*')
          .eq('submission_id', submission.id)
          .maybeSingle();

        if (error) {
          console.error('Error fetching review decision:', error);
          setReviewDecision(null);
        } else {
          setReviewDecision(data as ReviewDecision | null);
        }
      } catch (err: any) {
        console.error('Unexpected error fetching review decision:', err);
        setReviewDecision(null);
      }
    };

    fetchReviewDecision();
  }, [submission?.id, supabase, user?.id]);

  // Lazily load regulatory basis codes the first time the admin chooses the
  // "Required" continuing-review determination.
  //
  // The in-flight guard is a ref, NOT state: an earlier version kept
  // regulatoryCodesLoading in this effect's dependency array with a
  // `cancelled` cleanup, so setting loading=true re-fired the cleanup of the
  // very effect that started the request, the resolved fetch was treated as
  // cancelled, and the loading flag never cleared ("Loading..." forever).
  useEffect(() => {
    if (!crUiEnabled || crDetermination !== 'required') {
      return;
    }
    if (regulatoryCodesRequestedRef.current) {
      return;
    }

    regulatoryCodesRequestedRef.current = true;
    setRegulatoryCodesLoading(true);
    listRegulatoryBasisCodes(supabase)
      .then((rows) => {
        setRegulatoryCodes(rows as RegulatoryBasisCodeRow[]);
        setRegulatoryCodesLoaded(true);
        if (rows.length === 0) {
          // The service returns [] on errors too; let the admin retry rather
          // than dead-ending the decision form.
          regulatoryCodesRequestedRef.current = false;
        }
      })
      .finally(() => {
        setRegulatoryCodesLoading(false);
      });
  }, [crUiEnabled, crDetermination, regulatoryCodesRetry, supabase]);

  const toggleRegulatoryBasisCode = (code: string) => {
    setCrRegulatoryBasisCodes((prev) =>
      prev.includes(code) ? prev.filter((existing) => existing !== code) : [...prev, code]
    );
  };

  // Generate signed URLs for active documents


  // Generate signed URLs for inactive documents


  // Update submission field with authenticated client
  const updateSubmissionField = async (field: string, value: string | boolean) => {
    if (!submission) return;
    if (!user) {
      console.error('No authenticated user found');
      setUpdateStatus('error');
      return;
    }

    setUpdateStatus('updating');

    try {
      // Use authenticated client to update submission
      const { data, error } = await supabase
        .from('submissions')
        .update({
          [field]: value,
          updated_at: new Date().toISOString()
        })
        .eq('id', submission.id)
        .select();

      if (error) {
        console.error(`Error updating ${field}:`, error);
        setUpdateStatus('error');
        return;
      }

      if (!data || data.length === 0) {
        console.error('No rows were updated');
        setUpdateStatus('error');
        return;
      }

      setSubmission({
        ...submission,
        [field]: value,
        updated_at: new Date().toISOString()
      });

      setUpdateStatus('success');

      // Set timeout to clear success status
      setTimeout(() => {
        setUpdateStatus('idle');
      }, 3000);

    } catch (err) {
      console.error(`Error updating ${field}:`, err);
      setUpdateStatus('error');

      // Set timeout to clear error status
      setTimeout(() => {
        setUpdateStatus('idle');
      }, 3000);
    }
  };

  // New function to add feedback item
  const addFeedbackItem = () => {
    if (feedbackItems.length < 20) {
      setFeedbackItems([...feedbackItems, { type: 'required_change', text: '' }]);
    }
  };

  // New function to remove feedback item
  const removeFeedbackItem = (index: number) => {
    if (feedbackItems.length > 1) {
      const newItems = [...feedbackItems];
      newItems.splice(index, 1);
      setFeedbackItems(newItems);
    }
  };

  // New function to update feedback item
  const updateFeedbackItem = (index: number, field: 'type' | 'text', value: string) => {
    const newItems = [...feedbackItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setFeedbackItems(newItems);
  };

  // Modified submit review function
  const handleReviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!submission) return;
    if (!user) {
      console.error('No authenticated user found');
      setReviewSubmitStatus('error');
      return;
    }

    // Input validation
    const cycleNumber = parseInt(cycle, 10);
    if (isNaN(cycleNumber) || cycleNumber <= 0) {
      alert('Please enter a valid positive number for the cycle');
      return;
    }

    // Validate all feedback items have text with minimum length
    const invalidItems = feedbackItems.filter(item => item.text.trim().length < 10);
    if (invalidItems.length > 0) {
      alert('All feedback items must have at least 10 characters');
      return;
    }

    // Ensure at least one feedback item exists
    if (feedbackItems.length === 0) {
      alert('Please add at least one feedback item');
      return;
    }

    setReviewSubmitStatus('submitting');

    try {
      // Group feedback items by type
      const groupedFeedback = feedbackItems.reduce((acc, item) => {
        if (!acc[item.type]) {
          acc[item.type] = [];
        }
        acc[item.type].push(item.text.trim());
        return acc;
      }, {} as Record<string, string[]>);

      // Transform to required structure
      const formattedFeedback = Object.entries(groupedFeedback).map(([type, items]) => ({
        type,
        feedback_items: items
      }));

      // Prepare the new review cycle entry
      const newEntry = {
        cycle: cycleNumber,
        feedback: formattedFeedback,
        'feedback-timestamp': new Date().toISOString()
      };

      // Get current review data or create initial structure
      let currentReviewData = submission.review_data || { review_cycles: [] };

      // Ensure currentReviewData is an object with review_cycles property
      if (typeof currentReviewData !== 'object' || !currentReviewData) {
        currentReviewData = { review_cycles: [] };
      }

      // Add new entry to review cycles array
      const updatedReviewData = {
        review_cycles: [...(currentReviewData as any).review_cycles, newEntry]
      };

      // Update in database
      const { error } = await supabase
        .from('submissions')
        .update({
          review_data: updatedReviewData,
          updated_at: new Date().toISOString()
        })
        .eq('id', submission.id)
        .select();

      if (error) {
        console.error('Error updating review data:', error);
        setReviewSubmitStatus('error');

        // Set timeout to clear error status
        setTimeout(() => {
          setReviewSubmitStatus('idle');
        }, 3000);

        return;
      }

      // Update local state
      setSubmission({
        ...submission,
        review_data: updatedReviewData,
        updated_at: new Date().toISOString()
      });

      // Reset form
      setCycle('');
      setFeedbackItems([{ type: 'required_change', text: '' }]);
      setReviewSubmitStatus('success');

      // Set timeout to clear success status
      setTimeout(() => {
        setReviewSubmitStatus('idle');
      }, 3000);

    } catch (err) {
      console.error('Error submitting review data:', err);
      setReviewSubmitStatus('error');

      // Set timeout to clear error status
      setTimeout(() => {
        setReviewSubmitStatus('idle');
      }, 3000);
    }
  };

  // Handle decision file changes
  const handleDecisionFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDecisionFileError(null);

    const files = e.target.files;
    if (!files || files.length === 0) {
      setDecisionFile(null);
      return;
    }

    const file = files[0];

    // Validate file type
    if (file.type !== 'application/pdf') {
      setDecisionFileError('Only PDF files are allowed');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setDecisionFileError('File size must be less than 10MB');
      return;
    }

    setDecisionFile(file);
  };

  // Remove decision file
  const handleRemoveDecisionFile = () => {
    setDecisionFile(null);
    setDecisionFileError(null);

    // Reset file input
    const fileInput = document.getElementById('decisionFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const resetAdminDocumentUploadForm = () => {
    setAdminUploadFiles([]);
    setAdminUploadError(null);
    if (adminDocumentFileInputRef.current) {
      adminDocumentFileInputRef.current.value = '';
    }
  };

  const handleAdminUploadDocumentTypeChange = (nextType: string) => {
    if (!ADMIN_DOCUMENT_UPLOAD_TYPE_OPTIONS.includes(nextType)) {
      return;
    }

    setAdminUploadDocumentType(nextType);
    setAdminUploadStatus('idle');
    resetAdminDocumentUploadForm();
  };

  const handleAdminUploadFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAdminUploadError(null);
    setAdminUploadStatus('idle');

    const files = Array.from(e.target.files || []);
    if (files.length === 0) {
      setAdminUploadFiles([]);
      return;
    }

    if (adminUploadDocumentType === 'doc_protocol' && files.length > 1) {
      setAdminUploadError('Upload only one protocol PDF at a time.');
      setAdminUploadFiles([]);
      if (adminDocumentFileInputRef.current) {
        adminDocumentFileInputRef.current.value = '';
      }
      return;
    }

    const hasInvalidType = files.some((file) => file.type !== 'application/pdf');
    if (hasInvalidType) {
      setAdminUploadError('Only PDF files are allowed.');
      setAdminUploadFiles([]);
      if (adminDocumentFileInputRef.current) {
        adminDocumentFileInputRef.current.value = '';
      }
      return;
    }

    const hasInvalidSize = files.some((file) => file.size > MAX_ADMIN_DOCUMENT_UPLOAD_SIZE_BYTES);
    if (hasInvalidSize) {
      setAdminUploadError('Each file must be less than 10MB.');
      setAdminUploadFiles([]);
      if (adminDocumentFileInputRef.current) {
        adminDocumentFileInputRef.current.value = '';
      }
      return;
    }

    setAdminUploadFiles(files);
  };

  const handleAdminUserDocumentUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!submission || !user) {
      setAdminUploadError('You must be logged in as an admin to upload documents.');
      setAdminUploadStatus('error');
      return;
    }

    if (!ADMIN_DOCUMENT_UPLOAD_TYPE_OPTIONS.includes(adminUploadDocumentType)) {
      setAdminUploadError('Select a valid user-uploaded document type.');
      setAdminUploadStatus('error');
      return;
    }

    if (adminUploadFiles.length === 0) {
      setAdminUploadError('Select at least one PDF to upload.');
      setAdminUploadStatus('error');
      return;
    }

    if (adminUploadDocumentType === 'doc_protocol' && adminUploadFiles.length > 1) {
      setAdminUploadError('Upload only one protocol PDF at a time.');
      setAdminUploadStatus('error');
      return;
    }

    setAdminUploadStatus('uploading');
    setAdminUploadError(null);

    const previousProtocol = adminUploadDocumentType === 'doc_protocol' ? documents.find(doc => doc.type === 'doc_protocol' && doc.is_active) : undefined;
    if (previousProtocol && !window.confirm(`Replace ${previousProtocol.filename} with ${adminUploadFiles[0].name}? The previous file will be preserved as inactive.`)) {
      setAdminUploadStatus('idle'); return;
    }
    const remaining = [...adminUploadFiles];
    try {
      for (const file of adminUploadFiles) {
        if (adminUploadDocumentType === 'amendment_decision_letter') {
          const filePath = `${submission.id}/${crypto.randomUUID()}.${getSafeFileExtension(file.name)}`;
          const { error: uploadError } = await supabase.storage.from(getAdminUploadBucket(adminUploadDocumentType))
            .upload(filePath, file, { upsert: false, contentType: file.type });
          if (uploadError) throw uploadError;
          const { error: insertError } = await supabase.from('documents').insert({
            id: crypto.randomUUID(), submission_id: submission.id, user_id: null,
            filename: file.name, file_path: filePath, type: adminUploadDocumentType, is_active: true,
          });
          if (insertError) throw insertError; // Preserve bytes if the acknowledgement is uncertain.
        } else {
          let documentId = adminUploadIds.current.get(file);
          if (!documentId) { documentId = crypto.randomUUID(); adminUploadIds.current.set(file, documentId); }
          const uploaded = await uploadSingleDocument(file, submission.id, submission.user_id || user.id,
            adminUploadDocumentType, { documentId, inactive: true });
          if (isErrorResponse(uploaded)) throw new Error(uploaded.message);
          await manageDocument(uploaded.id, previousProtocol ? 'replace' : 'activate', adminUploadDocumentType, previousProtocol?.id);
        }
        remaining.shift();
        setAdminUploadFiles([...remaining]);
      }
      resetAdminDocumentUploadForm();
      setAdminUploadStatus('success');
      setTimeout(() => setAdminUploadStatus('idle'), 3000);
    } catch (err: any) {
      setAdminUploadError(err?.message || 'Failed to upload document. Successful uploads were preserved; retry the remaining files.');
      setAdminUploadStatus('error');
    } finally { await fetchSubmissionAndDocuments(); }
  };

  const handleUpdateDocumentActiveStatus = async (docId: string, nextActive: boolean) => {
    if (!submission) return;
    setDocumentActionInFlightId(docId);
    try {
      const target = [...documents, ...inactiveDocuments].find(doc => doc.id === docId);
      if (!target) throw new Error('Refresh the document list and try again.');
      if (target.type?.startsWith('doc_')) {
        const previous = nextActive && target.type === 'doc_protocol' ? documents.find(doc => doc.type === 'doc_protocol' && doc.id !== docId) : undefined;
        if (previous && !window.confirm(`Replace ${previous.filename} with ${target.filename}? The previous file will be preserved as inactive.`)) return;
        await manageDocument(docId, previous ? 'replace' : nextActive ? 'restore' : 'remove', target.type, previous?.id);
      } else {
        const { data, error } = await supabase.from('documents').update({ is_active: nextActive })
          .eq('id', docId).eq('submission_id', submission.id).select('id').single();
        if (error || !data) throw error || new Error('The document update was not saved.');
      }
      await fetchSubmissionAndDocuments();
    } catch (err: any) { window.alert(err?.message || 'The document update failed. Please retry.'); }
    finally { setDocumentActionInFlightId(null); }
  };

  const handleUpdateAdditionalFileType = async (docId: string, newType: string) => {
    if (!submission || !USER_DOCUMENT_TYPE_OPTIONS.includes(newType)) return;
    setDocumentTypeInFlightId(docId);
    try {
      const target = [...documents, ...inactiveDocuments].find(doc => doc.id === docId);
      const previous = target?.is_active && newType === 'doc_protocol' ? documents.find(doc => doc.type === 'doc_protocol' && doc.id !== docId) : undefined;
      if (previous && !window.confirm(`Replace ${previous.filename} with ${target?.filename}? The previous file will be preserved as inactive.`)) return;
      await manageDocument(docId, previous ? 'replace' : 'classify', newType, previous?.id);
      await fetchSubmissionAndDocuments();
    } catch (err: any) { window.alert(err?.message || 'The document type update failed. Please retry.'); }
    finally { setDocumentTypeInFlightId(null); }
  };

  const handleStampedIcfFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStampedIcfFileError(null);
    const files = Array.from(e.target.files || []);
    if (files.length === 0) {
      setStampedIcfFiles([]);
      return;
    }

    const hasInvalidType = files.some((file) => file.type !== 'application/pdf');
    if (hasInvalidType) {
      setStampedIcfFileError('Please upload a PDF file');
      return;
    }

    const hasInvalidSize = files.some((file) => file.size > 10 * 1024 * 1024);
    if (hasInvalidSize) {
      setStampedIcfFileError('Each file must be less than 10MB');
      return;
    }

    setStampedIcfFiles(files);
  };

  const toggleSelectedConsentDocument = (docId: string) => {
    setSelectedExistingConsentIds((prev) => {
      if (prev.includes(docId)) {
        return prev.filter((id) => id !== docId);
      }
      return [...prev, docId];
    });
  };

  const resetStampedIcfModal = () => {
    setShowStampedIcfModal(false);
    setStampedIcfMode('existing');
    setStampedIcfFiles([]);
    setSelectedExistingConsentIds([]);
    setStampedIcfFileError(null);
  };

  const openStampedIcfModal = () => {
    const consentIds = documents
      .filter((doc) => doc.type === 'doc_consent_form')
      .map((doc) => doc.id);
    setStampedIcfError(null);
    setStampedIcfStatus('idle');
    setStampedIcfApprovalDate(reviewDecision?.approval_date || getLocalIsoDate());
    setStampedIcfExpiryDate(reviewDecision?.expiry_date || '');
    setSelectedExistingConsentIds(consentIds);
    setStampedIcfFiles([]);
    setStampedIcfFileError(null);
    setShowStampedIcfModal(true);
  };

  const handleGenerateStampedIcf = async () => {
    if (!submission || !user) {
      return;
    }

    const currentActiveConsentDocuments = documents.filter((doc) => doc.type === 'doc_consent_form');
    let consentDocumentIdsToStamp: string[] = [];

    if (!reviewDecision) {
      setStampedIcfError('Stamped ICF generation requires a submitted decision.');
      setStampedIcfStatus('error');
      return;
    }

    if (reviewDecision.decision === 'DENIED') {
      setStampedIcfError('Stamped ICF generation is not available for denied decisions.');
      setStampedIcfStatus('error');
      return;
    }

    if (!stampedIcfApprovalDate) {
      setStampedIcfError('Please enter an approval date for the stamp.');
      setStampedIcfStatus('error');
      return;
    }

    if (stampedIcfExpiryDate && stampedIcfExpiryDate <= stampedIcfApprovalDate) {
      setStampedIcfError('Expiry date must be after the approval date.');
      setStampedIcfStatus('error');
      return;
    }

    if (stampedIcfMode === 'upload' && stampedIcfFiles.length === 0) {
      setStampedIcfFileError('Please upload at least one consent form PDF.');
      setStampedIcfStatus('error');
      return;
    }

    if (stampedIcfMode === 'existing' && currentActiveConsentDocuments.length === 0) {
      setStampedIcfError('No active consent form is available to stamp.');
      setStampedIcfStatus('error');
      return;
    }

    if (stampedIcfMode === 'existing' && selectedExistingConsentIds.length === 0) {
      setStampedIcfError('Select at least one active consent form to stamp.');
      setStampedIcfStatus('error');
      return;
    }

    setStampedIcfStatus('submitting');
    setStampedIcfError(null);

    try {
      const { error: decisionUpdateError } = await supabase
        .from('review_decisions')
        .update({
          approval_date: stampedIcfApprovalDate,
          expiry_date: stampedIcfExpiryDate ? stampedIcfExpiryDate : null,
          updated_at: new Date().toISOString()
        })
        .eq('id', reviewDecision.id);

      if (decisionUpdateError) {
        console.error('Error updating decision dates:', decisionUpdateError);
        setStampedIcfError('Failed to update approval/expiry dates.');
        setStampedIcfStatus('error');
        return;
      }

      setReviewDecision({
        ...reviewDecision,
        approval_date: stampedIcfApprovalDate,
        expiry_date: stampedIcfExpiryDate ? stampedIcfExpiryDate : null,
        updated_at: new Date().toISOString()
      });

      if (stampedIcfMode === 'upload') {
        for (const stampedIcfFile of stampedIcfFiles) {
          const fileExt = stampedIcfFile.name.split('.').pop() || 'pdf';
          const filePath = `${submission.id}/${crypto.randomUUID()}.${fileExt}`;

          const { error: uploadError } = await supabase.storage
            .from('user-uploaded-docs')
            .upload(filePath, stampedIcfFile, {
              cacheControl: '3600',
              upsert: false,
              contentType: stampedIcfFile.type,
            });

          if (uploadError) {
            console.error('Error uploading consent form:', uploadError);
            setStampedIcfError('Failed to upload one or more consent forms.');
            setStampedIcfStatus('error');
            return;
          }

          const newConsentId = crypto.randomUUID();
          const { error: insertError } = await supabase
            .from('documents')
            .insert({
              id: newConsentId,
              submission_id: submission.id,
              user_id: submission.user_id,
              filename: stampedIcfFile.name,
              file_path: filePath,
              uploaded_at: new Date().toISOString(),
              type: 'doc_consent_form',
              is_active: true
            });

          if (insertError) {
            console.error('Error inserting consent form record:', insertError);
            // Keep the uploaded file: the metadata insert may have committed despite a lost response.
            setStampedIcfError('Failed to save consent form metadata.');
            setStampedIcfStatus('error');
            return;
          }

          consentDocumentIdsToStamp.push(newConsentId);
        }
      } else {
        const activeConsentIdSet = new Set(currentActiveConsentDocuments.map((doc) => doc.id));
        consentDocumentIdsToStamp = selectedExistingConsentIds.filter((docId) => activeConsentIdSet.has(docId));

        if (consentDocumentIdsToStamp.length === 0) {
          setStampedIcfError('Selected consent forms are no longer active. Refresh and try again.');
          setStampedIcfStatus('error');
          return;
        }
      }

      const { error: stampError } = await supabase.functions.invoke('stamped_icf_generator', {
        body: {
          submission_id: submission.id,
          mode: 'manual',
          consent_document_ids: consentDocumentIdsToStamp,
        }
      });

      if (stampError) {
        console.error('Error generating stamped ICF:', stampError);
        setStampedIcfError('Failed to generate the stamped ICF.');
        setStampedIcfStatus('error');
        return;
      }

      setStampedIcfStatus('success');
      resetStampedIcfModal();
      await fetchSubmissionAndDocuments();
      setTimeout(() => {
        setStampedIcfStatus('idle');
      }, 3000);
    } catch (err) {
      console.error('Unexpected error generating stamped ICF:', err);
      setStampedIcfError('Unexpected error while generating stamped ICF.');
      setStampedIcfStatus('error');
    }
  };

  // Format date for filename (DDMMMYYYY)
  const formatDateForFilename = (date: Date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const year = date.getFullYear();
    return `${day}${month}${year}`;
  };

  // Modified handleDecisionSubmit to include file upload
  const handleDecisionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!submission) return;
    if (!user) {
      console.error('No authenticated user found');
      setDecisionSubmitStatus('error');
      return;
    }

    // Clear any prior continuing-review metadata error before re-validating.
    setCrMetadataError(null);

    // Input validation
    if (!decision) {
      alert('Please select a decision');
      return;
    }

    if (!approvalDate) {
      alert('Please select an approval date');
      return;
    }

    if (expiryDate && expiryDate <= approvalDate) {
      alert('Expiry date must be after the approval date');
      return;
    }

    if (decisionData.trim().length < 500) {
      alert('Decision data must be at least 500 characters');
      return;
    }

    // File validation
    if (!decisionFile) {
      setDecisionFileError('Please upload a decision letter');
      return;
    }

    // Continuing-review determination pre-validation (only when the UI flag is on
    // and the admin actually recorded a determination). Mirrors server checks.
    if (crUiEnabled && crDetermination === 'required') {
      const intervalNum = parseInt(crIntervalMonths, 10);
      if (isNaN(intervalNum) || intervalNum < 1 || intervalNum > 60) {
        alert('Continuing review interval must be a whole number of months between 1 and 60.');
        return;
      }
      if (crRegulatoryBasisCodes.length === 0) {
        alert('Select at least one regulatory basis code for the continuing review determination.');
        return;
      }
      if (!expiryDate) {
        alert('Expiry date is required when continuing review is required.');
        return;
      }
      if (expiryDate > addMonthsToIsoDate(approvalDate, intervalNum)) {
        alert('Expiry date must be on or before the approval date plus the continuing review interval.');
        return;
      }
      const hasFdaBasis = crRegulatoryBasisCodes.some((code) => {
        const row = regulatoryCodes.find((entry) => entry.code === code);
        return row ? row.authority === 'FDA' : code.startsWith('FDA');
      });
      if (hasFdaBasis && intervalNum > 12) {
        alert('FDA-regulated continuing reviews cannot have an interval greater than 12 months.');
        return;
      }
    }

    if (crUiEnabled && crDetermination === 'not_required' && !crNotRequiredReason) {
      alert('Select a reason why continuing review is not required.');
      return;
    }

    setDecisionSubmitStatus('submitting');
    setFileUploading(true);

    try {
      // Create a new UUID for the decision record
      const decisionId = crypto.randomUUID();

      // Create new decision record in review_decisions table (for both flows)
      const { error: decisionError } = await supabase
        .from('review_decisions')
        .insert({
          id: decisionId,
          submission_id: submission.id,
          reviewer_id: user.id,
          decision: decision as DecisionType,
          decision_data: decisionData.trim(),
          approval_date: approvalDate,
          expiry_date: expiryDate ? expiryDate : null,
          created_at: new Date().toISOString()
        } as ReviewDecisionInsert);

      if (decisionError) {
        console.error('Error creating decision record:', decisionError);
        setDecisionSubmitStatus('error');
        setFileUploading(false);
        setTimeout(() => {
          setDecisionSubmitStatus('idle');
        }, 3000);
        return;
      }

      setReviewDecision({
        id: decisionId,
        submission_id: submission.id,
        reviewer_id: user.id,
        decision: decision as DecisionType,
        decision_data: decisionData.trim(),
        approval_date: approvalDate,
        expiry_date: expiryDate ? expiryDate : null,
        created_at: new Date().toISOString(),
        updated_at: null
      } as ReviewDecision);

      // Additive: record the continuing-review determination on the just-created
      // decision. The legacy INSERT above is unchanged; this is a separate atomic
      // RPC. If it fails we DO NOT roll back the decision — we surface an inline
      // error so the admin can remediate the metadata for the existing decision.
      if (crUiEnabled && crDetermination !== '') {
        try {
          if (crDetermination === 'required') {
            await updateContinuingReviewDecisionMetadata(supabase, {
              reviewDecisionId: decisionId,
              continuingReviewRequired: true,
              intervalMonths: parseInt(crIntervalMonths, 10),
              windowDays: crWindowDays ? parseInt(crWindowDays, 10) : null,
              basis: crBasis.trim() ? crBasis.trim() : null,
              rationale: crRationale.trim() ? crRationale.trim() : null,
              regulatoryBasisCodes: crRegulatoryBasisCodes,
              notRequiredReason: null,
            });
          } else {
            await updateContinuingReviewDecisionMetadata(supabase, {
              reviewDecisionId: decisionId,
              continuingReviewRequired: false,
              notRequiredReason: crNotRequiredReason,
              rationale: crRationale.trim() ? crRationale.trim() : null,
              regulatoryBasisCodes: [],
              intervalMonths: null,
              windowDays: null,
              basis: null,
            });
          }
        } catch (metaErr: any) {
          console.error(
            `Continuing review metadata update FAILED after the decision was created. ` +
              `The decision row exists (id=${decisionId}) and was NOT rolled back; ` +
              `the continuing-review determination must be re-recorded manually.`,
            metaErr
          );
          setCrMetadataError(
            `The decision was saved, but the continuing review determination could not be recorded ` +
              `(${metaErr?.message || 'unknown error'}). The decision exists (id ${decisionId}). ` +
              `Do not resubmit the decision — re-record the continuing review metadata from the admin tools.`
          );
        }
      }

      if (!requiresPiApproval) {
        // ISSUE IMMEDIATELY: Upload final decision letter and set status to DECISION_ISSUED

        // Create custom filename for the decision letter
        const today = new Date();
        const formattedDate = formatDateForFilename(today);
        const refName = submission.data && typeof submission.data === 'object' && 'ref_name' in submission.data
          ? String(submission.data.ref_name)
          : 'NO_REF';
        const customFilename = `${refName}_Timely IRB decision letter_${formattedDate}.pdf`;

        // Path in storage bucket (folder = submission ID)
        const filePath = `${submission.id}/${customFilename}`;

        // Upload file to Supabase storage
        const { error: fileUploadError } = await supabase.storage
          .from('tempus-created-docs')
          .upload(filePath, decisionFile!, {
            cacheControl: '3600',
            upsert: false
          });

        if (fileUploadError) {
          console.error('Error uploading decision letter:', fileUploadError);
          setDecisionSubmitStatus('error');
          setFileUploading(false);
          setTimeout(() => {
            setDecisionSubmitStatus('idle');
          }, 3000);
          return;
        }

        // Create document record in documents table
        const documentId = crypto.randomUUID();
        const { error: documentError } = await supabase
          .from('documents')
          .insert({
            id: documentId,
            submission_id: submission.id,
            user_id: null,
            filename: customFilename,
            file_path: filePath,
            uploaded_at: new Date().toISOString(),
            type: 'decision_letter',
            is_active: true
          });

        if (documentError) {
          console.error('Error creating document record:', documentError);
          setDecisionSubmitStatus('error');
          setFileUploading(false);
          setTimeout(() => {
            setDecisionSubmitStatus('idle');
          }, 3000);
          return;
        }

        // Update submission with decision_id and change status to DECISION_ISSUED
        const { error: submissionError } = await supabase
          .from('submissions')
          .update({
            decision_id: decisionId,
            status: 'DECISION_ISSUED',
            updated_at: new Date().toISOString()
          })
          .eq('id', submission.id)
          .select();

        if (submissionError) {
          console.error('Error updating submission with decision:', submissionError);
          setDecisionSubmitStatus('error');
          setFileUploading(false);
          setTimeout(() => {
            setDecisionSubmitStatus('idle');
          }, 3000);
          return;
        }

        // Update local state
        setSubmission({
          ...submission,
          decision_id: decisionId,
          status: 'DECISION_ISSUED',
          updated_at: new Date().toISOString()
        });

        // Update status in UI state to match
        setStatus('DECISION_ISSUED');

      } else {
        // WAIT FOR PI APPROVAL: Upload staged decision letter and set decision_letter_upload flag

        // Create filename for staged decision letter
        const stagedFilename = `decision_letter_${submission.id}_staged.pdf`;
        const stagedFilePath = `decision_letters/${stagedFilename}`;

        // Upload staged decision letter to decision_letters folder
        const { error: fileUploadError } = await supabase.storage
          .from('tempus-created-docs')
          .upload(stagedFilePath, decisionFile!, {
            cacheControl: '3600',
            upsert: false
          });

        if (fileUploadError) {
          console.error('Error uploading internal decision letter:', fileUploadError);
          setDecisionSubmitStatus('error');
          setFileUploading(false);
          setTimeout(() => {
            setDecisionSubmitStatus('idle');
          }, 3000);
          return;
        }

        // Update submission with decision_id and set decision_letter_upload to TRUE
        const { error: submissionError } = await supabase
          .from('submissions')
          .update({
            decision_id: decisionId,
            decision_letter_upload: true,
            updated_at: new Date().toISOString()
          })
          .eq('id', submission.id)
          .select();

        if (submissionError) {
          console.error('Error updating submission with decision_letter_upload flag:', submissionError);
          setDecisionSubmitStatus('error');
          setFileUploading(false);
          setTimeout(() => {
            setDecisionSubmitStatus('idle');
          }, 3000);
          return;
        }

        // Update local state
        setSubmission({
          ...submission,
          decision_id: decisionId,
          decision_letter_upload: true,
          updated_at: new Date().toISOString()
        } as any);
      }

      // Reset form
      setDecision('');
      setDecisionData('');
      setDecisionFile(null);
      setApprovalDate(getLocalIsoDate());
      setExpiryDate('');
      // Reset continuing-review determination fields. crMetadataError is left
      // intact so a failed metadata write stays visible for remediation.
      setCrDetermination('');
      setCrIntervalMonths('');
      setCrRegulatoryBasisCodes([]);
      setCrWindowDays('60');
      setCrBasis('');
      setCrRationale('');
      setCrNotRequiredReason('');
      setDecisionSubmitStatus('success');
      setFileUploading(false);

      setTimeout(() => {
        setDecisionSubmitStatus('idle');
      }, 3000);

    } catch (err) {
      console.error('Error submitting decision data:', err);
      setDecisionSubmitStatus('error');
      setFileUploading(false);

      setTimeout(() => {
        setDecisionSubmitStatus('idle');
      }, 3000);
    }
  };

  // Add new function to update PI approval status message
  const updatePiApprovalStatusMsg = async () => {
    if (!submission) return;
    if (!user) {
      console.error('No authenticated user found');
      setPiApprovalMsgStatus('error');
      return;
    }

    setPiApprovalMsgStatus('updating');

    try {
      // Use authenticated client to update submission
      const { data, error } = await supabase
        .from('submissions')
        .update({
          pi_approval_status_msg: piApprovalStatusMsg,
          updated_at: new Date().toISOString()
        })
        .eq('id', submission.id)
        .select();

      if (error) {
        console.error(`Error updating PI approval status message:`, error);
        setPiApprovalMsgStatus('error');
        return;
      }

      if (!data || data.length === 0) {
        console.error('No rows were updated');
        setPiApprovalMsgStatus('error');
        return;
      }

      setSubmission({
        ...submission,
        pi_approval_status_msg: piApprovalStatusMsg,
        updated_at: new Date().toISOString()
      });

      setPiApprovalMsgStatus('success');

      // Set timeout to clear success status
      setTimeout(() => {
        setPiApprovalMsgStatus('idle');
      }, 3000);

    } catch (err) {
      console.error(`Error updating PI approval status message:`, err);
      setPiApprovalMsgStatus('error');

      // Set timeout to clear error status
      setTimeout(() => {
        setPiApprovalMsgStatus('idle');
      }, 3000);
    }
  };

  // Show loading spinner during auth loading
  if (authLoading) {
    return (
      <>
        <AdminNavBar user={user} onSignOut={handleSignOut} />
        <div className="p-8 mt-16">
          <h1 className="text-xl font-bold mb-4">Loading...</h1>
          <div className="flex items-center justify-start">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#F55D3E]"></div>
            <span className="ml-2 text-gray-600">Verifying your account</span>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <AdminNavBar user={user} onSignOut={handleSignOut} />
        <div className="p-8 mt-16">
          <h1 className="text-xl font-bold mb-4">Loading submission...</h1>
          <div className="flex items-center justify-start">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#F55D3E]"></div>
            <span className="ml-2 text-gray-600">Please wait</span>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <AdminNavBar user={user} onSignOut={handleSignOut} />
        <div className="p-8 mt-16">
          <h1 className="text-xl font-bold mb-4">Error</h1>
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="px-4 py-2 bg-[#F55D3E] text-white rounded hover:bg-[#e04d30] transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </>
    );
  }

  if (!submission) {
    return (
      <>
        <AdminNavBar user={user} onSignOut={handleSignOut} />
        <div className="p-8 mt-16">
          <h1 className="text-xl font-bold mb-4">Submission Not Found</h1>
          <p className="text-gray-600 mb-6">
            The submission either does not exist or you do not have permission to view it.
          </p>
          <button
            onClick={() => navigate('/admin')}
            className="px-4 py-2 bg-[#F55D3E] text-white rounded hover:bg-[#e04d30] transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </>
    );
  }

  const activeConsentDocuments = documents
    .filter((doc) => doc.type === 'doc_consent_form')
    .sort((a, b) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime());
  const isAdminProtocolUpload = adminUploadDocumentType === 'doc_protocol';

  const isContinuingReview = submission.submission_type === 'CONTINUING_REVIEW';
  const submissionTypeLabel = getSubmissionTypeLabel(submission.submission_type);
  const submissionData =
    submission.data && typeof submission.data === 'object'
      ? (submission.data as Record<string, any>)
      : {};

  // Group regulatory basis codes by authority for the grouped multi-select.
  const groupedRegulatoryCodes = regulatoryCodes.reduce((acc, row) => {
    (acc[row.authority] = acc[row.authority] || []).push(row);
    return acc;
  }, {} as Record<string, RegulatoryBasisCodeRow[]>);

  // Continuing-review determination gates the decision submit only when the UI
  // flag is on AND a determination was chosen — so the legacy path is unaffected.
  const crDecisionBlocksSubmit =
    crUiEnabled &&
    ((crDetermination === 'required' &&
      (!expiryDate || !crIntervalMonths || crRegulatoryBasisCodes.length === 0)) ||
      (crDetermination === 'not_required' && !crNotRequiredReason));

  return (
    <>
      <AdminNavBar user={user} onSignOut={handleSignOut} />
      <div className="p-8 mt-16 max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            {isContinuingReview && (
              <div className="flex items-center gap-3 mb-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  {submissionTypeLabel}
                  {submission.continuing_review_sequence
                    ? ` #${submission.continuing_review_sequence}`
                    : ''}
                </span>
                {submission.parent_submission_id && (
                  <a
                    href={adminReviewRoute(submission.parent_submission_id)}
                    className="text-sm text-[#F55D3E] hover:underline"
                  >
                    View source submission &rarr;
                  </a>
                )}
              </div>
            )}
            <h2 className="text-2xl font-bold text-[#343a40]">
              {submission.data && typeof submission.data === 'object' && 'protocol_title' in submission.data
                ? String(submission.data.protocol_title)
                : 'Submission Review'}
            </h2>
            <h3 className="text-xl text-[#343a40] mt-1">
              Sponsor: {submission.data && typeof submission.data === 'object' && 'sponsor_name' in submission.data
                ? String(submission.data.sponsor_name)
                : ''}
            </h3>
            <h4 className="text-lg text-gray-600">
              PI: {submission.data && typeof submission.data === 'object' && 'pi_first_name' in submission.data
                ? String(submission.data.pi_first_name)
                : ''} {submission.data && typeof submission.data === 'object' && 'pi_last_name' in submission.data
                  ? String(submission.data.pi_last_name)
                  : ''}
            </h4>
            <h4 className="text-lg text-gray-600">
              PI Email: {submission.data && typeof submission.data === 'object' && 'pi_email' in submission.data
                ? String(submission.data.pi_email)
                : ''}
            </h4>
            <h4 className="text-lg text-gray-600">
              Billing Email: {submission.data && typeof submission.data === 'object' && 'invoice_email' in submission.data
                ? String(submission.data.invoice_email)
                : ''}
            </h4>
          </div>
          <div className="flex flex-col gap-2">
            {isContinuingReview ? (
              <p className="px-4 py-2 bg-gray-100 text-gray-600 rounded-md text-sm max-w-xs">
                LLM analysis is not available for continuing reviews yet.
              </p>
            ) : (
              <button
                onClick={() => window.open(`/admin/LLMOutputsDisplay/${submission.id}`, '_blank')}
                className="px-4 py-2 bg-[#F55D3E] text-white rounded-md hover:bg-[#E54D2E] transition-colors text-sm font-medium"
              >
                View LLM Outputs
              </button>
            )}
          </div>
        </div>

        {/* Update status notification */}
        {updateStatus === 'success' && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 animate-fadeOut">
            <p className="text-green-700">Update successful</p>
          </div>
        )}

        {updateStatus === 'error' && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <p className="text-red-700">Update failed. Please try again.</p>
          </div>
        )}

        <div className="mb-8">
          <ShareAccessManager submissionId={submission.id} />
        </div>

        {/* Source study summary (continuing reviews only) */}
        {isContinuingReview && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <button
              type="button"
              onClick={() => setShowSourceSummary((v) => !v)}
              aria-expanded={showSourceSummary}
              className="w-full flex items-center justify-between text-left text-xl font-semibold text-[#343a40] mb-4 pb-2 border-b border-gray-200"
            >
              <span>Source Submission Summary</span>
              <svg
                className={`w-5 h-5 transform transition-transform ${showSourceSummary ? 'rotate-180' : 'rotate-0'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showSourceSummary && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Protocol Title</p>
                  <p className="text-gray-900">{submissionData.protocol_title || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Reference Name</p>
                  <p className="text-gray-900">{submissionData.ref_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Source Approval Date</p>
                  <p className="text-gray-900">{submissionData.source_approval_date || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Source Expiry Date</p>
                  <p className="text-gray-900">{submissionData.source_expiry_date || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Source Decision</p>
                  <p className="text-gray-900">{submissionData.source_decision || 'N/A'}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Submission Info Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-[#343a40] mb-6 pb-2 border-b border-gray-200">
            Submission Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic info - not editable */}
            <div>
              <div className="mb-4">
                <p className="text-sm text-gray-500">Submission ID</p>
                <p className="font-mono">{submission.id}</p>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-500">User ID</p>
                <p className="font-mono">{submission.user_id}</p>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-500">Decision ID</p>
                <p className="font-mono">{submission.decision_id || 'N/A'}</p>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-500">Created At</p>
                <p>{new Date(submission.created_at).toLocaleString()}</p>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-500">Last Updated</p>
                <p>{new Date(submission.updated_at).toLocaleString()}</p>
              </div>
            </div>

            {/* Editable Fields */}
            <div>
              {/* Classification */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Classification
                </label>
                <div className="flex items-center space-x-2">
                  <select
                    value={classification}
                    onChange={(e) => setClassification(e.target.value)}
                    className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                  >
                    <option value="">-- Select Classification --</option>
                    <option value="EXEMPT">Exempt</option>
                    <option value="EXPEDITED">Expedited</option>
                    <option value="FULL_BOARD">Full Board</option>
                  </select>
                  <button
                    onClick={() => updateSubmissionField('classification', classification)}
                    disabled={classification === submission.classification}
                    className={`px-3 py-1 rounded text-sm ${classification === submission.classification
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                  >
                    Save
                  </button>
                </div>
              </div>

              {/* Status */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <div className="flex items-center space-x-2">
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                  >
                    <option value="">-- Select Status --</option>
                    <option value="DRAFT">Draft</option>
                    <option value="IRB_REVIEW">IRB Review</option>
                    <option value="NEEDS_ACTION">Needs Action</option>
                    <option value="DECISION_ISSUED">Decision Issued</option>
                  </select>
                  <button
                    onClick={() => updateSubmissionField('status', status)}
                    disabled={status === submission.status}
                    className={`px-3 py-1 rounded text-sm ${status === submission.status
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                  >
                    Save
                  </button>
                </div>
              </div>

              {/* Ready for PI Approval */}
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Ready for PI Approval?
                  </label>
                  <div className="flex items-center space-x-2">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={readyForPiApproval}
                        onChange={(e) => setReadyForPiApproval(e.target.checked)}
                        className="rounded text-[#F55D3E] focus:ring-[#F55D3E]"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {readyForPiApproval ? 'Yes' : 'No'}
                      </span>
                    </label>
                    <button
                      onClick={() => updateSubmissionField('ready_for_pi_approval', readyForPiApproval)}
                      disabled={readyForPiApproval === submission.ready_for_pi_approval}
                      className={`px-3 py-1 rounded text-sm ${readyForPiApproval === submission.ready_for_pi_approval
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>

              {/* PI Approval Status Message */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PI Approval Status Message
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={piApprovalStatusMsg}
                    onChange={(e) => setPiApprovalStatusMsg(e.target.value)}
                    className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                    placeholder="Enter status message for PI approval"
                  />
                  <button
                    onClick={updatePiApprovalStatusMsg}
                    disabled={piApprovalMsgStatus === 'updating'}
                    className={`px-3 py-1 rounded text-sm ${piApprovalMsgStatus === 'updating'
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                  >
                    {piApprovalMsgStatus === 'updating' ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </span>
                    ) : (
                      'Save'
                    )}
                  </button>
                </div>
                {piApprovalMsgStatus === 'success' && (
                  <p className="mt-1 text-xs text-green-600">Status message saved successfully</p>
                )}
                {piApprovalMsgStatus === 'error' && (
                  <p className="mt-1 text-xs text-red-600">Failed to save status message</p>
                )}
              </div>

              {/* Payment Completed */}
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Payment Completed
                  </label>
                  <div className="flex items-center space-x-2">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={paymentCompleted}
                        onChange={(e) => setPaymentCompleted(e.target.checked)}
                        className="rounded text-[#F55D3E] focus:ring-[#F55D3E]"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {paymentCompleted ? 'Yes' : 'No'}
                      </span>
                    </label>
                    <button
                      onClick={() => updateSubmissionField('payment_completed', paymentCompleted)}
                      disabled={paymentCompleted === submission.payment_completed}
                      className={`px-3 py-1 rounded text-sm ${paymentCompleted === submission.payment_completed
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>

              {/* PI Approval */}
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    PI Approval Completed?
                  </label>
                  <div className="flex items-center space-x-2">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={piApproval}
                        onChange={(e) => setPiApproval(e.target.checked)}
                        className="rounded text-[#F55D3E] focus:ring-[#F55D3E]"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {piApproval ? 'Yes' : 'No'}
                      </span>
                    </label>
                    <button
                      onClick={() => updateSubmissionField('pi_approval', piApproval)}
                      disabled={piApproval === submission.pi_approval}
                      className={`px-3 py-1 rounded text-sm ${piApproval === submission.pi_approval
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-[#343a40] mb-6 pb-2 border-b border-gray-200">
            Upload User Document
          </h2>
          {adminUploadStatus === 'success' && (
            <div className="bg-green-50 border-l-4 border-green-500 p-3 mb-4">
              <p className="text-green-700 text-sm">Document uploaded successfully.</p>
            </div>
          )}
          {adminUploadStatus === 'error' && adminUploadError && (
            <div className="bg-red-50 border-l-4 border-red-500 p-3 mb-4">
              <p className="text-red-700 text-sm">{adminUploadError}</p>
            </div>
          )}
          <form onSubmit={handleAdminUserDocumentUpload} className="grid grid-cols-1 lg:grid-cols-[minmax(220px,320px)_1fr_auto] gap-4 items-end">
            <div>
              <label htmlFor="adminUploadDocumentType" className="block text-sm font-medium text-gray-700 mb-1">
                Document Type
              </label>
              <select
                id="adminUploadDocumentType"
                value={adminUploadDocumentType}
                onChange={(e) => handleAdminUploadDocumentTypeChange(e.target.value)}
                disabled={adminUploadStatus === 'uploading'}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 text-sm"
              >
                {ADMIN_DOCUMENT_UPLOAD_TYPE_OPTIONS.map((typeOption) => (
                  <option key={typeOption} value={typeOption}>
                    {DOCUMENT_TYPE_LABELS[typeOption] || typeOption}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="adminUploadDocumentFile" className="block text-sm font-medium text-gray-700 mb-1">
                PDF File
              </label>
              <input
                id="adminUploadDocumentFile"
                ref={adminDocumentFileInputRef}
                type="file"
                accept="application/pdf"
                multiple={!isAdminProtocolUpload}
                onChange={handleAdminUploadFileChange}
                disabled={adminUploadStatus === 'uploading'}
                className="block w-full text-sm text-gray-600"
              />
              {adminUploadFiles.length > 0 && (
                <ul className="mt-2 text-xs text-gray-500 space-y-1">
                  {adminUploadFiles.map((file) => (
                    <li key={`${file.name}-${file.size}-${file.lastModified}`}>
                      {file.name}
                    </li>
                  ))}
                </ul>
              )}
              {adminUploadError && adminUploadStatus !== 'error' && (
                <p className="mt-2 text-xs text-red-600">{adminUploadError}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={adminUploadStatus === 'uploading' || adminUploadFiles.length === 0}
              className={`px-4 py-2 rounded text-white text-sm ${adminUploadStatus === 'uploading' || adminUploadFiles.length === 0
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-[#F55D3E] hover:bg-[#e04d30] transition-colors'
                }`}
            >
              {adminUploadStatus === 'uploading' ? 'Uploading...' : 'Upload'}
            </button>
          </form>
        </div>

        {/* Active Documents Table */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <button
            type="button"
            onClick={() => setShowActiveDocs((v) => !v)}
            aria-expanded={showActiveDocs}
            className="w-full flex items-center justify-between text-left text-xl font-semibold text-[#343a40] mb-6 pb-2 border-b border-gray-200"
          >
            <span>Active Submission Documents</span>
            <svg
              className={`w-5 h-5 transform transition-transform ${showActiveDocs ? 'rotate-180' : 'rotate-0'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showActiveDocs && (
            documents.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 italic">No active documents found for this submission</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Filename
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        File Path
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Storage
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Uploaded At
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {documents.map((doc) => {
                      const bucketName = getDocumentBucket(doc.type || '');
                      const isTempusCreated = bucketName === 'tempus-created-docs';

                      return (
                        <tr key={doc.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {doc.filename}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <DocumentDownloadLink document={doc} client={supabase} />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {doc.type === 'doc_additional_files' || doc.type === 'doc_consent_form' ? (
                              <select
                                value={doc.type || 'doc_additional_files'}
                                onChange={(e) => handleUpdateAdditionalFileType(doc.id, e.target.value)}
                                disabled={documentTypeInFlightId === doc.id}
                                className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 text-sm"
                              >
                                {getTypeEditOptions(doc.type).map((typeOption) => (
                                  <option key={typeOption} value={typeOption}>
                                    {DOCUMENT_TYPE_LABELS[typeOption] || typeOption}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              DOCUMENT_TYPE_LABELS[doc.type || ''] || doc.type
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {isTempusCreated ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Timely created
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                User uploaded
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(doc.uploaded_at).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              type="button"
                              onClick={() => handleUpdateDocumentActiveStatus(doc.id, false)}
                              disabled={documentActionInFlightId === doc.id}
                              className={`px-3 py-1 rounded text-white text-xs ${documentActionInFlightId === doc.id
                                ? 'bg-gray-300 cursor-not-allowed'
                                : 'bg-amber-600 hover:bg-amber-500'
                                }`}
                            >
                              {documentActionInFlightId === doc.id ? 'Updating...' : 'Deactivate'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>

        {/* Inactive Documents Table */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <button
            type="button"
            onClick={() => setShowInactiveDocs((v) => !v)}
            aria-expanded={showInactiveDocs}
            className="w-full flex items-center justify-between text-left text-xl font-semibold text-[#343a40] mb-6 pb-2 border-b border-gray-200"
          >
            <span>Inactive Submission Documents</span>
            <svg
              className={`w-5 h-5 transform transition-transform ${showInactiveDocs ? 'rotate-180' : 'rotate-0'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showInactiveDocs && (
            inactiveDocuments.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 italic">No inactive documents found for this submission</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Filename
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        File Path
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Storage
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Uploaded At
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {inactiveDocuments.map((doc) => {
                      const bucketName = getDocumentBucket(doc.type || '');
                      const isTempusCreated = bucketName === 'tempus-created-docs';

                      return (
                        <tr key={doc.id} className="bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {doc.filename}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <DocumentDownloadLink document={doc} client={supabase} />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {doc.type === 'doc_additional_files' || doc.type === 'doc_consent_form' ? (
                              <select
                                value={doc.type || 'doc_additional_files'}
                                onChange={(e) => handleUpdateAdditionalFileType(doc.id, e.target.value)}
                                disabled={documentTypeInFlightId === doc.id}
                                className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 text-sm bg-white"
                              >
                                {getTypeEditOptions(doc.type).map((typeOption) => (
                                  <option key={typeOption} value={typeOption}>
                                    {DOCUMENT_TYPE_LABELS[typeOption] || typeOption}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              DOCUMENT_TYPE_LABELS[doc.type || ''] || doc.type
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {isTempusCreated ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 opacity-75">
                                Timely created
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 opacity-75">
                                User uploaded
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {new Date(doc.uploaded_at).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              type="button"
                              onClick={() => handleUpdateDocumentActiveStatus(doc.id, true)}
                              disabled={documentActionInFlightId === doc.id}
                              className={`px-3 py-1 rounded text-white text-xs ${documentActionInFlightId === doc.id
                                ? 'bg-gray-300 cursor-not-allowed'
                                : 'bg-emerald-600 hover:bg-emerald-500'
                                }`}
                            >
                              {documentActionInFlightId === doc.id ? 'Updating...' : 'Activate'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>

        {/* Submission Data */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <button
            type="button"
            onClick={() => setShowSubmissionJson((v) => !v)}
            aria-expanded={showSubmissionJson}
            className="w-full flex items-center justify-between text-left text-xl font-semibold text-[#343a40] mb-6 pb-2 border-b border-gray-200"
          >
            <span>Submission JSON Data (Active)</span>
            <svg
              className={`w-5 h-5 transform transition-transform ${showSubmissionJson ? 'rotate-180' : 'rotate-0'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showSubmissionJson && (

            <div className="bg-gray-50 p-4 rounded-md overflow-auto max-h-96">
              <pre className="text-sm font-mono text-gray-700 whitespace-pre-wrap">
                {submission.data && typeof submission.data === 'object' && Object.keys(submission.data).length === 0 ? (
                  <span className="italic text-gray-500">No data available</span>
                ) : (
                  JSON.stringify(submission.data, null, 2)
                    .split('\n')
                    .map((line, index) => {
                      // Add syntax highlighting
                      let coloredLine = line;
                      // Highlight keys
                      coloredLine = coloredLine.replace(
                        /"([^"]+)":/g,
                        '<span class="text-blue-600">"$1"</span>:'
                      );
                      // Highlight string values
                      coloredLine = coloredLine.replace(
                        /: "([^"]*)"/g,
                        ': <span class="text-green-600">"$1"</span>'
                      );
                      // Highlight boolean/null values
                      coloredLine = coloredLine.replace(
                        /: (true|false|null)/g,
                        ': <span class="text-purple-600">$1</span>'
                      );
                      // Highlight numbers
                      coloredLine = coloredLine.replace(
                        /: (\d+(\.\d+)?)/g,
                        ': <span class="text-orange-600">$1</span>'
                      );

                      return (
                        <div
                          key={index}
                          className="leading-6"
                          dangerouslySetInnerHTML={{ __html: coloredLine }}
                        />
                      );
                    })
                )}
              </pre>
            </div>
          )}
        </div>

        {/* Review Data Section - Modified to use horizontal rows */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-[#343a40] mb-6 pb-2 border-b border-gray-200">
            Add IRB Review Feedback Items
          </h2>

          <div className="flex flex-col space-y-6">
            {/* Top Row - Form */}
            <div className="bg-gray-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-[#343a40] mb-4">Add Review Feedback</h3>

              {reviewSubmitStatus === 'success' && (
                <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 animate-fadeOut">
                  <p className="text-green-700">Review data submitted successfully</p>
                </div>
              )}

              {reviewSubmitStatus === 'error' && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                  <p className="text-red-700">Failed to submit review data. Please try again.</p>
                </div>
              )}

              <form onSubmit={handleReviewSubmit}>
                <div className="mb-4">
                  <label htmlFor="cycle" className="block text-sm font-medium text-gray-700 mb-1">
                    Cycle <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="cycle"
                    min="1"
                    step="1"
                    value={cycle}
                    onChange={(e) => setCycle(e.target.value)}
                    className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full max-w-xs"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">Enter a positive integer</p>
                </div>

                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Feedback Items <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={addFeedbackItem}
                      disabled={feedbackItems.length >= 20}
                      className={`px-3 py-1 text-sm rounded-md ${feedbackItems.length >= 20
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                    >
                      Add New Item
                    </button>
                  </div>

                  <div className="space-y-4 max-h-[500px] overflow-y-auto p-2">
                    {feedbackItems.map((item, index) => (
                      <div key={index} className="p-3 border border-gray-200 rounded-md bg-white">
                        <div className="flex justify-between items-start mb-2">
                          <div className="w-1/3 pr-2">
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Feedback Type
                            </label>
                            <select
                              value={item.type}
                              onChange={(e) => updateFeedbackItem(index, 'type', e.target.value)}
                              className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full text-sm"
                              required
                            >
                              <option value="required_change">Required Change</option>
                              <option value="optional_change">Optional Change</option>
                              <option value="note">Note</option>
                            </select>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeFeedbackItem(index)}
                            disabled={feedbackItems.length <= 1}
                            className={`ml-2 px-2 py-1 rounded-md ${feedbackItems.length <= 1
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-red-100 text-red-600 hover:bg-red-200'
                              }`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Feedback Text (10-500 characters)
                          </label>
                          <textarea
                            value={item.text}
                            onChange={(e) => updateFeedbackItem(index, 'text', e.target.value)}
                            className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full text-sm"
                            rows={2}
                            maxLength={5000}
                            placeholder="Enter feedback (minimum 10 characters)"
                            required
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            {item.text.length}/5000 characters
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={
                      !cycle ||
                      feedbackItems.some(item => item.text.trim().length < 10) ||
                      reviewSubmitStatus === 'submitting'
                    }
                    className={`px-4 py-2 rounded text-white ${!cycle ||
                      feedbackItems.some(item => item.text.trim().length < 10) ||
                      reviewSubmitStatus === 'submitting'
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-[#F55D3E] hover:bg-[#e04d30] transition-colors'
                      }`}
                  >
                    {reviewSubmitStatus === 'submitting' ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </span>
                    ) : (
                      'Submit Review Data'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Feedback and Responses Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-[#343a40] mb-6 pb-2 border-b border-gray-200">
            Data from Previous Review Cycles
          </h2>

          {submission?.review_data && typeof submission.review_data === 'object' &&
            'review_cycles' in submission.review_data &&
            Array.isArray(submission.review_data.review_cycles) &&
            submission.review_data.review_cycles.length > 0 ? (
            <div className="space-y-8">
              {/* Get all cycles and sort them in descending order */}
              {[...((submission.review_data.review_cycles as unknown) as ReviewCycle[])]
                .sort((a, b) => b.cycle - a.cycle)
                .map((cycle, index, allCycles) => (
                  <div key={`cycle-${cycle.cycle}`}>
                    {/* Cycle header */}
                    <div className="mb-4 bg-gray-100 p-3 rounded-md">
                      <h3 className="font-medium text-gray-800">
                        Review Cycle {cycle.cycle}
                        <span className="ml-2 text-sm text-gray-500">
                          ({new Date(cycle['feedback-timestamp']).toLocaleDateString()})
                        </span>
                      </h3>
                    </div>

                    {/* Feedback component */}
                    <FeedbackAndResponses
                      submissionId={submission.id}
                      cycleNumber={cycle.cycle}
                      mode="view"
                    />

                    {/* Add separator between cycles (except after the last one) */}
                    {index < allCycles.length - 1 && (
                      <div className="border-t border-dashed border-gray-300 my-6 pt-2 flex justify-center">
                        <span className="px-4 py-1 bg-gray-100 text-gray-500 text-sm rounded-full">
                          Previous cycle below
                        </span>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          ) : (
            <div className="bg-gray-50 p-4 rounded-md text-gray-600">
              <p>No feedback cycles are available for this submission.</p>
            </div>
          )}
        </div>

        {/* Decision Data Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-[#343a40] mb-6 pb-2 border-b border-gray-200">
            Decision Data
          </h2>

          {decisionSubmitStatus === 'success' && (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 animate-fadeOut">
              <p className="text-green-700">Decision submitted successfully</p>
            </div>
          )}

          {decisionSubmitStatus === 'error' && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
              <p className="text-red-700">Failed to submit decision. Please try again.</p>
            </div>
          )}

          <form onSubmit={handleDecisionSubmit}>
            <div className="grid grid-cols-1 gap-6">
              {/* Requires PI Approval */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Requires PI Approval
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={requiresPiApproval}
                    onChange={(e) => setRequiresPiApproval(e.target.checked)}
                    className="text-[#F55D3E] focus:ring-[#F55D3E]"
                  />
                  <span className="ml-2">Issue decision after PI approval</span>
                </label>
                <div className="mt-3 bg-blue-50 border-l-4 border-blue-400 p-4">
                  <p className="text-blue-700 text-sm">
                    {requiresPiApproval
                      ? 'Decision letter is staged and issued when PI approval is recorded.'
                      : 'Decision letter is issued immediately (no PI approval required).'}
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="decision" className="block text-sm font-medium text-gray-700 mb-1">
                  Final Decision <span className="text-red-500">*</span>
                </label>
                <select
                  id="decision"
                  value={decision}
                  onChange={(e) => setDecision(e.target.value)}
                  className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                  required
                >
                  <option value="">-- Select Decision --</option>
                  <option value="APPROVED">Approved</option>
                  <option value="COND_APPROVED">Conditionally Approved</option>
                  <option value="DENIED">Denied</option>
                </select>
              </div>

              <div className="mb-4">
                <label htmlFor="decisionData" className="block text-sm font-medium text-gray-700 mb-1">
                  Decision Data <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="decisionData"
                  rows={6}
                  value={decisionData}
                  onChange={(e) => setDecisionData(e.target.value)}
                  className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                  placeholder="Enter detailed decision data (minimum 500 characters)"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  {decisionData.length} / 5000 characters (minimum 500)
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="approvalDate" className="block text-sm font-medium text-gray-700 mb-1">
                    Approval Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="approvalDate"
                    type="date"
                    value={approvalDate}
                    onChange={(e) => setApprovalDate(e.target.value)}
                    className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700 mb-1">
                    Expiry Date (optional)
                  </label>
                  <input
                    id="expiryDate"
                    type="date"
                    value={expiryDate}
                    min={approvalDate || undefined}
                    onChange={(e) => setExpiryDate(e.target.value)}
                    className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                  />
                  <p className="mt-1 text-xs text-gray-500">Leave blank if there is no expiry.</p>
                </div>
              </div>

              {/* Continuing review determination (renders for all submission types
                  when the continuing-reviews UI flag is on; renders nothing and
                  leaves the legacy decision flow untouched when the flag is off). */}
              {crUiEnabled && (
                <fieldset className="border border-gray-200 rounded-md p-4">
                  <legend className="text-sm font-semibold text-gray-700 px-1">
                    Continuing review determination
                  </legend>

                  {crMetadataError && (
                    <div className="bg-amber-50 border-l-4 border-amber-500 p-3 mb-4">
                      <p className="text-amber-800 text-sm">{crMetadataError}</p>
                    </div>
                  )}

                  <div className="flex flex-col gap-2 mb-4">
                    <label className="inline-flex items-center text-sm text-gray-700">
                      <input
                        type="radio"
                        name="crDetermination"
                        value=""
                        checked={crDetermination === ''}
                        onChange={() => setCrDetermination('')}
                        className="text-[#F55D3E] focus:ring-[#F55D3E]"
                      />
                      <span className="ml-2">Not specified (legacy — no determination recorded)</span>
                    </label>
                    <label className="inline-flex items-center text-sm text-gray-700">
                      <input
                        type="radio"
                        name="crDetermination"
                        value="required"
                        checked={crDetermination === 'required'}
                        onChange={() => setCrDetermination('required')}
                        className="text-[#F55D3E] focus:ring-[#F55D3E]"
                      />
                      <span className="ml-2">Continuing review required</span>
                    </label>
                    <label className="inline-flex items-center text-sm text-gray-700">
                      <input
                        type="radio"
                        name="crDetermination"
                        value="not_required"
                        checked={crDetermination === 'not_required'}
                        onChange={() => setCrDetermination('not_required')}
                        className="text-[#F55D3E] focus:ring-[#F55D3E]"
                      />
                      <span className="ml-2">Continuing review not required</span>
                    </label>
                  </div>

                  {crDetermination === 'required' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="crIntervalMonths" className="block text-sm font-medium text-gray-700 mb-1">
                            Interval (months) <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="crIntervalMonths"
                            type="number"
                            min={1}
                            max={60}
                            step={1}
                            value={crIntervalMonths}
                            onChange={(e) => setCrIntervalMonths(e.target.value)}
                            className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                          />
                          <p className="mt-1 text-xs text-gray-500">1–60. FDA bases require ≤ 12.</p>
                        </div>
                        <div>
                          <label htmlFor="crWindowDays" className="block text-sm font-medium text-gray-700 mb-1">
                            Reminder window (days)
                          </label>
                          <input
                            id="crWindowDays"
                            type="number"
                            min={1}
                            step={1}
                            value={crWindowDays}
                            onChange={(e) => setCrWindowDays(e.target.value)}
                            className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                          />
                          <p className="mt-1 text-xs text-gray-500">Defaults to 60.</p>
                        </div>
                      </div>

                      <div>
                        <p className="block text-sm font-medium text-gray-700 mb-1">
                          Regulatory basis codes <span className="text-red-500">*</span>
                        </p>
                        {regulatoryCodesLoading && (
                          <p className="text-xs text-gray-500">Loading regulatory basis codes...</p>
                        )}
                        {regulatoryCodesLoaded && !regulatoryCodesLoading && regulatoryCodes.length === 0 && (
                          <p className="text-xs text-gray-500">
                            Could not load regulatory basis codes.{' '}
                            <button
                              type="button"
                              className="text-blue-600 hover:underline"
                              onClick={() => {
                                setRegulatoryCodesLoaded(false);
                                setRegulatoryCodesRetry((attempt) => attempt + 1);
                              }}
                            >
                              Retry
                            </button>
                          </p>
                        )}
                        {regulatoryCodes.length > 0 && (
                          <div className="space-y-3 border border-gray-200 rounded-md p-3 max-h-60 overflow-y-auto">
                            {Object.entries(groupedRegulatoryCodes).map(([authority, codes]) => (
                              <div key={authority}>
                                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                  {authority}
                                </p>
                                <div className="mt-1 space-y-1">
                                  {codes.map((row) => (
                                    <label key={row.code} className="flex items-start gap-2 text-sm text-gray-700">
                                      <input
                                        type="checkbox"
                                        checked={crRegulatoryBasisCodes.includes(row.code)}
                                        onChange={() => toggleRegulatoryBasisCode(row.code)}
                                        className="mt-1 rounded text-[#F55D3E] focus:ring-[#F55D3E]"
                                      />
                                      <span>
                                        <span className="font-medium">{row.code}</span> — {row.description}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div>
                        <label htmlFor="crBasis" className="block text-sm font-medium text-gray-700 mb-1">
                          Basis (short)
                        </label>
                        <input
                          id="crBasis"
                          type="text"
                          value={crBasis}
                          onChange={(e) => setCrBasis(e.target.value)}
                          className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                          placeholder="Short basis summary"
                        />
                      </div>

                      <div>
                        <label htmlFor="crRationaleRequired" className="block text-sm font-medium text-gray-700 mb-1">
                          Rationale
                        </label>
                        <textarea
                          id="crRationaleRequired"
                          rows={3}
                          value={crRationale}
                          onChange={(e) => setCrRationale(e.target.value)}
                          className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                          placeholder="Reviewer rationale for the continuing review interval"
                        />
                      </div>
                    </div>
                  )}

                  {crDetermination === 'not_required' && (
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="crNotRequiredReason" className="block text-sm font-medium text-gray-700 mb-1">
                          Not-required reason <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="crNotRequiredReason"
                          value={crNotRequiredReason}
                          onChange={(e) => setCrNotRequiredReason(e.target.value)}
                          className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                        >
                          <option value="">-- Select reason --</option>
                          {CONTINUING_REVIEW_NOT_REQUIRED_REASONS.map((reason) => (
                            <option key={reason.value} value={reason.value}>
                              {reason.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="crRationaleNotRequired" className="block text-sm font-medium text-gray-700 mb-1">
                          Rationale
                        </label>
                        <textarea
                          id="crRationaleNotRequired"
                          rows={3}
                          value={crRationale}
                          onChange={(e) => setCrRationale(e.target.value)}
                          className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                          placeholder="Reviewer rationale for not requiring continuing review"
                        />
                      </div>
                    </div>
                  )}
                </fieldset>
              )}

              {/* File Upload Input */}
              <div className="mb-4">
                <label htmlFor="decisionFile" className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Decision Letter <span className="text-red-500">*</span>
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  {requiresPiApproval
                    ? 'This file will be staged until PI approval is recorded.'
                    : 'This file will be issued immediately.'}
                </p>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    {!decisionFile ? (
                      <>
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                          aria-hidden="true"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className="flex text-sm text-gray-600">
                          <label
                            htmlFor="decisionFile"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-[#F55D3E] hover:text-[#e04d30] focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[#F55D3E]"
                          >
                            <span>Upload a file</span>
                            <input
                              id="decisionFile"
                              name="decisionFile"
                              type="file"
                              className="sr-only"
                              accept="application/pdf"
                              onChange={handleDecisionFileChange}
                              required
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">PDF up to 10MB</p>
                      </>
                    ) : (
                      <div className="flex flex-col items-center">
                        <svg
                          className="h-10 w-10 text-green-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <p className="mt-2 text-sm text-gray-900 font-medium">
                          {decisionFile.name}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {(decisionFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                        <button
                          type="button"
                          onClick={handleRemoveDecisionFile}
                          className="mt-2 inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Remove file
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {decisionFileError && (
                  <p className="mt-1 text-sm text-red-600">{decisionFileError}</p>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={
                    !decision ||
                    decisionData.trim().length < 500 ||
                    !approvalDate ||
                    (expiryDate && expiryDate <= approvalDate) ||
                    !decisionFile ||
                    crDecisionBlocksSubmit ||
                    decisionSubmitStatus === 'submitting'
                  }
                  className={`px-4 py-2 rounded text-white ${!decision ||
                    decisionData.trim().length < 500 ||
                    !approvalDate ||
                    (expiryDate && expiryDate <= approvalDate) ||
                    !decisionFile ||
                    crDecisionBlocksSubmit ||
                    decisionSubmitStatus === 'submitting'
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-[#F55D3E] hover:bg-[#e04d30] transition-colors'
                    }`}
                >
                  {decisionSubmitStatus === 'submitting' ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {fileUploading ? 'Uploading file...' : 'Submitting...'}
                    </span>
                  ) : (
                    'Submit Decision'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-[#343a40] mb-4 pb-2 border-b border-gray-200">
            Stamped ICF (Manual)
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Generate stamped ICF documents using selected active consent forms or newly uploaded consent forms. If the decision
            is Conditional Approval, the stamped ICF will remain hidden from the PI until the decision is
            updated to Approved.
          </p>
          {stampedIcfStatus === 'success' && (
            <div className="bg-green-50 border-l-4 border-green-500 p-3 mb-4">
              <p className="text-green-700 text-sm">Stamped ICF generated successfully.</p>
            </div>
          )}
          {stampedIcfStatus === 'error' && stampedIcfError && (
            <div className="bg-red-50 border-l-4 border-red-500 p-3 mb-4">
              <p className="text-red-700 text-sm">{stampedIcfError}</p>
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              openStampedIcfModal();
            }}
            className="px-4 py-2 bg-[#F55D3E] text-white rounded hover:bg-[#e04d30] transition-colors"
          >
            Generate Stamped ICF
          </button>
        </div>

        {showStampedIcfModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Generate Stamped ICF</h3>
                <button
                  type="button"
                  onClick={resetStampedIcfModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  X
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Choose one mode for this run: stamp selected active consent forms, or upload one or more
                new consent forms and stamp those uploads.
              </p>
              <div className="space-y-4">
                <label className="flex items-start gap-3 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="stampedIcfMode"
                    value="existing"
                    checked={stampedIcfMode === 'existing'}
                    onChange={() => {
                      setStampedIcfMode('existing');
                      setStampedIcfFiles([]);
                      setSelectedExistingConsentIds(activeConsentDocuments.map((doc) => doc.id));
                      setStampedIcfFileError(null);
                    }}
                    className="mt-1 text-[#F55D3E] focus:ring-[#F55D3E]"
                  />
                  <span>Select one or more active consent forms on file.</span>
                </label>
                <label className="flex items-start gap-3 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="stampedIcfMode"
                    value="upload"
                    checked={stampedIcfMode === 'upload'}
                    onChange={() => {
                      setStampedIcfMode('upload');
                      setSelectedExistingConsentIds([]);
                      setStampedIcfFileError(null);
                    }}
                    className="mt-1 text-[#F55D3E] focus:ring-[#F55D3E]"
                  />
                  <span>Upload one or more new consent form PDFs before stamping.</span>
                </label>
                {stampedIcfMode === 'existing' && (
                  <div className="border border-gray-200 rounded-md p-3 max-h-52 overflow-y-auto">
                    {activeConsentDocuments.length === 0 ? (
                      <p className="text-xs text-gray-500">No active consent forms are available.</p>
                    ) : (
                      <div className="space-y-2">
                        {activeConsentDocuments.map((doc) => (
                          <label key={doc.id} className="flex items-start gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={selectedExistingConsentIds.includes(doc.id)}
                              onChange={() => toggleSelectedConsentDocument(doc.id)}
                              className="mt-1 rounded text-[#F55D3E] focus:ring-[#F55D3E]"
                            />
                            <span>
                              <span className="block font-medium text-gray-900">{doc.filename}</span>
                              <span className="block text-xs text-gray-500">
                                Uploaded {new Date(doc.uploaded_at).toLocaleString()}
                              </span>
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {stampedIcfMode === 'upload' && (
                  <div>
                    <input
                      type="file"
                      accept="application/pdf"
                      multiple
                      onChange={handleStampedIcfFileChange}
                      className="block w-full text-sm text-gray-600"
                    />
                    {stampedIcfFiles.length > 0 && (
                      <ul className="mt-2 text-xs text-gray-500 space-y-1">
                        {stampedIcfFiles.map((file) => (
                          <li key={`${file.name}-${file.size}`}>{file.name}</li>
                        ))}
                      </ul>
                    )}
                    {stampedIcfFileError && (
                      <p className="mt-2 text-xs text-red-600">{stampedIcfFileError}</p>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Approval Date (stamp)
                    </label>
                    <input
                      type="date"
                      value={stampedIcfApprovalDate}
                      onChange={(e) => setStampedIcfApprovalDate(e.target.value)}
                      className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Expiry Date (optional)
                    </label>
                    <input
                      type="date"
                      value={stampedIcfExpiryDate}
                      min={stampedIcfApprovalDate || undefined}
                      onChange={(e) => setStampedIcfExpiryDate(e.target.value)}
                      className="border-gray-300 rounded-md shadow-sm focus:border-[#F55D3E] focus:ring focus:ring-[#F55D3E] focus:ring-opacity-50 w-full"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  These dates overwrite the decision record and will be used in the stamp.
                </p>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={resetStampedIcfModal}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleGenerateStampedIcf}
                  disabled={stampedIcfStatus === 'submitting'}
                  className={`px-4 py-2 text-white rounded ${stampedIcfStatus === 'submitting'
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-[#F55D3E] hover:bg-[#e04d30] transition-colors'
                    }`}
                >
                  {stampedIcfStatus === 'submitting' ? 'Generating...' : 'Generate'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default withIdleTimer(AdminReview);
